import { batch } from 'react-redux';
import {
  FREE_WEEK_SKU,
  MONTHLY_ROKT_SERVICE_ID,
  BF_HOLIDAY_RENEWAL_SKU,
  BLACK_FRIDAY_PACKAGE_ID,
  HOLIDAY_2022_PACKAGE_ID,
  VA_INITIAL_FREE_YEAR_ID,
} from 'Utils/Constants';
import { logger } from 'Utils/Logger';
import {
  getPlansWithDppContent,
  handleBFHoliday2022RenewalPlan,
  handleFreeWeekPlan,
  handleGeneralPlans,
  handlePlanToSelect,
  handleRoktPlan,
  handleSheerIdPlans,
  handleVeteransAdvPlans,
} from 'Utils/Plans';
import {
  GET_PLANS_SUCCESS,
  GET_PLANS_REQUEST,
  SELECT_PLAN,
  CLEAR_PLANS,
  CLEAR_SELECTED_PLAN,
  DISABLE_FREE_PROMO,
  DISABLE_DEFAULT_PROMO,
  FETCHING_VETERANS_ADV_AFTER_SIGNUP,
} from '../Types';
import { shouldGetSheerIdPlans } from 'Utils/SheerIdHelpers';
import { clearSheerId, clearVeteransAdvError, clearVeteransAdvId } from 'State/Integrations/IntegrationsActions';
import { getMonthlyExperimentPlans, getSetMonthly30DayFreeTrialVariant } from 'Utils/Analytics/Amplitude';
import { VeteransAdvStatus, shouldGetVAPlans } from 'Utils/VeteransAdvHelpers';

// action creators
export const fetchPlansRequest = (payload) => ({ type: GET_PLANS_REQUEST, payload });
export const fetchPlans = (payload) => ({ type: GET_PLANS_SUCCESS, payload });
export const setSelectedPlan = (payload) => ({ type: SELECT_PLAN, payload });
export const clearPlans = (payload) => ({ type: CLEAR_PLANS, payload });
export const clearSelectedPlan = () => ({ type: CLEAR_SELECTED_PLAN });
export const disableFreePromo = () => ({ type: DISABLE_FREE_PROMO });
export const disableDefaultPromo = () => ({ type: DISABLE_DEFAULT_PROMO });
export const willFetchVeteransAdvAfterSignup = (payload) => ({ type: FETCHING_VETERANS_ADV_AFTER_SIGNUP, payload });

// action thunks
export const selectPlan =
  (planId = '', plans) =>
  async (dispatch, getState) => {
    const {
      plans: { allPlans, selectedPlanId },
      appConfig: {
        defaultConfig: { defaultPackage },
      },
      purchaseMode: { requirePlanSelection },
    } = getState();
    const planChoices = plans || allPlans;
    logger.started('select plan', { planId });
    const defaultSelectedPlan = requirePlanSelection ? '' : defaultPackage;
    const serviceId = planId || selectedPlanId || defaultSelectedPlan;
    const selectedPlan = planChoices.find(({ packageID }) => packageID === serviceId) || null;
    if (selectedPlan) {
      logger.completed('select plan', { planId, selectedPlan, selectedPlanId });
      dispatch(
        setSelectedPlan({
          selectedPlan,
          selectedPlanId: serviceId,
          selectedPlanPrice: selectedPlan.planPrice || 0,
        })
      );
    } else {
      const error = `Plan not found for planId: '${serviceId}'`;
      logger.error('select plan', { selectedPlan, selectedPlanId, requirePlanSelection, serviceId }, error);
    }
  };

export const selectRestartPlan = (selectedPlan) => (dispatch) => {
  logger.started('select restart plan', { selectedPlan });
  dispatch(setSelectedPlan({ selectedPlan }));
  logger.completed('select restart plan', { selectedPlan });
};

export const getPlans = (preselectedPlan) => async (dispatch, getState) => {
  logger.started('get plans');
  dispatch(fetchPlansRequest());

  const {
    user: { isAuthenticated },
    plans: { selectedPlanId, willFetchVeteransAdvAfterSignup: veteransAdvAfterSignup },
    subscription,
    integrations: {
      bango,
      veteransAdv: { member_id, error: vaError },
    },
  } = getState();

  let planToSelect = preselectedPlan || selectedPlanId || '';

  let getVeteransAdvPlans;
  if (vaError) {
    getVeteransAdvPlans = VeteransAdvStatus.ERROR;
  } else if (member_id) {
    getVeteransAdvPlans = VeteransAdvStatus.VALID;
  } else {
    getVeteransAdvPlans = await shouldGetVAPlans(isAuthenticated, subscription, bango);
  }
  const getSheerIdPlans = await shouldGetSheerIdPlans(isAuthenticated, subscription.activeSubscriptionId);

  let plans = { backendPlans: [], dppPlans: [] };

  if (getVeteransAdvPlans === VeteransAdvStatus.VALID) {
    dispatch(clearVeteransAdvError());

    plans = await handleVeteransAdvPlans(subscription);
    if (plans.backendPlans.length > 0) {
      if (!planToSelect) planToSelect = VA_INITIAL_FREE_YEAR_ID;

      /* if not getting any plans (most likely due to an error) then just offer general market plans instead */
    } else {
      dispatch(clearVeteransAdvId());
      plans = await handleGeneralPlans(subscription, planToSelect);
    }
  } else if (getSheerIdPlans) {
    plans = await handleSheerIdPlans(subscription);
    /* if not getting any plans (most likely due to an error) then just offer general market plans instead */
    if (!plans.backendPlans.length) {
      clearSheerId();
      plans = await handleGeneralPlans(subscription, planToSelect);
    }
  } else if (planToSelect.toUpperCase() === MONTHLY_ROKT_SERVICE_ID && !subscription.hasActiveSubscription) {
    plans = await handleRoktPlan();
    //TODO: remove FREE_WEEK_SKU logic below after special promo plans expire (May 30 2023)
  } else if (subscription.activeSubscriptionId === FREE_WEEK_SKU) {
    plans = await handleFreeWeekPlan(FREE_WEEK_SKU);
  } else {
    plans = await handleGeneralPlans(subscription, planToSelect);
  }

  if (veteransAdvAfterSignup) {
    // because this is a weird case where user enters the payment page directly without selecting a plan
    //    beforehand, normally they're redirected back to plan selection. so we need to set a flag in the
    //    store so the payment page waits for the veterans adv to be fetched and selected
    // regardless of whether or not VA plan was successfully fetched/selected above, it is the VA query param
    //    that set this flag initially, so we should disable it here
    dispatch(willFetchVeteransAdvAfterSignup(false));
  }

  // for the Black Friday/Holiday 2022 Promo renewal
  // if user is subscribed to the Black Friday/Holiday 2022 plan, fetch renewal and replace two year plan
  if (
    [BLACK_FRIDAY_PACKAGE_ID, HOLIDAY_2022_PACKAGE_ID, BF_HOLIDAY_RENEWAL_SKU].includes(
      subscription.activeSubscriptionId
    )
  ) {
    const alreadySubscribedToRenewal = subscription.activeSubscriptionId == BF_HOLIDAY_RENEWAL_SKU;
    plans = await handleBFHoliday2022RenewalPlan(plans, alreadySubscribedToRenewal);
  }

  //Merging plan info from BE with plan info from DPP
  let allPlans = getPlansWithDppContent(plans);

  const monthExperimentVariant = getSetMonthly30DayFreeTrialVariant();
  // for the Monthly 30 Day Free Trial experiment
  allPlans = getMonthlyExperimentPlans(monthExperimentVariant, allPlans);

  //finding plan to select
  const planId = handlePlanToSelect(planToSelect, allPlans, subscription.activeSubscriptionId);

  batch(() => {
    dispatch(fetchPlans({ allPlans }));
    if (planId) {
      dispatch(selectPlan(planId, allPlans));
    } else {
      dispatch(clearSelectedPlan());
    }
  });

  logger.completed('get plans', { allPlans, planToSelect: planId });
};
